/**
 * #1287 — the trader's own OddsPapi books ("My books"), stored PER
 * AUTHENTICATED USER.
 *
 * A trader picks the bookmakers they trust; Game Detail's OddsPapi table then
 * shows those books and a separately labelled consensus of only them. No
 * selection (the default, and what Reset returns to) leaves the table exactly
 * as it is for everyone else.
 *
 * Same lifecycle as `liveHotkeys` / `savedGames` / `minimizedGames`, and for
 * the same reason (#728's note: a list read during render needs the in-memory
 * state, the ownership gate, `hydrateFor` and the `purgeUserCaches` entry). It
 * is not a field in the browser-wide `displaySettings` blob: the next trader
 * on a shared browser must start with no selection rather than inherit one,
 * and a tab on an older build rewrites that whole blob from its own field
 * list, which would drop this one (#724).
 *
 * Ownership is enforced on READ (`selectOwnedPersonalBooks`): hydration is an
 * effect and can lag a user change by a frame, and the fallback is no
 * selection — the shipped table — never another account's books.
 *
 * Presentation only: nothing here reaches the relay, the organization or
 * another trader, and it never changes the feed, hydra's eligibility, order
 * prices or any automated input. Identifiers are stored as given (OddsPapi
 * bookmaker slugs); a selected book outside hydra's census stays selected
 * and is marked as such.
 */
import { create } from 'zustand';
import { storageKey } from '@/lib/infra/storage';

/** Frozen empty selection: the shared "not opted in" identity, so a gated
 *  read does not hand subscribers a fresh array every render. */
const NO_BOOKS: readonly string[] = Object.freeze([]);

/** An OddsPapi bookmaker slug as the wire spells them (`fanduel`,
 *  `bookmaker.eu`, `sports411.ag`). Anything else in storage is dropped. */
const SLUG = /^[a-z0-9][a-z0-9.-]{0,63}$/;
/** More than the census plus every listed extra; a bound on what storage can
 *  hand the table, not a product limit. */
const MAX_BOOKS = 64;

function storageKeyFor(userId: string): string {
  return storageKey(`galactus:personalBooks:${userId}`);
}

/** Keep valid, distinct slugs in their given order. */
export function sanitizePersonalBooks(raw: unknown): readonly string[] {
  if (!Array.isArray(raw)) return NO_BOOKS;
  const out: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string' || !SLUG.test(entry) || out.includes(entry)) continue;
    out.push(entry);
    if (out.length >= MAX_BOOKS) break;
  }
  return out.length === 0 ? NO_BOOKS : out;
}

function load(userId: string): readonly string[] {
  try {
    const raw = localStorage.getItem(storageKeyFor(userId));
    if (!raw) return NO_BOOKS;
    const parsed: unknown = JSON.parse(raw);
    // A value from a future shape, or corrupted in place, reads as no
    // selection: the full table is the safe direction.
    if (parsed == null || typeof parsed !== 'object' || (parsed as { v?: unknown }).v !== 1) return NO_BOOKS;
    return sanitizePersonalBooks((parsed as { books?: unknown }).books);
  } catch {
    // Unavailable or malformed localStorage — no selection.
    return NO_BOOKS;
  }
}

function persist(userId: string, books: readonly string[]): void {
  try {
    if (books.length === 0) localStorage.removeItem(storageKeyFor(userId));
    else localStorage.setItem(storageKeyFor(userId), JSON.stringify({ v: 1, books }));
  } catch {
    // Full or unavailable localStorage — the session still works in memory.
  }
}

interface PersonalBooksState {
  /** Who the loaded selection belongs to. Null between logout and hydration. */
  userId: string | null;
  books: readonly string[];
  hydrateFor: (userId: string | null) => void;
  /** Add the book if it is not selected, remove it if it is. */
  toggleBook: (slug: string) => void;
  /** Back to no selection: the table shows every book again. */
  reset: () => void;
}

export const usePersonalBooks = create<PersonalBooksState>((set, get) => ({
  userId: null,
  books: NO_BOOKS,

  hydrateFor: (userId) => {
    if (get().userId === userId) return;
    if (!userId) {
      set({ userId: null, books: NO_BOOKS });
      return;
    }
    set({ userId, books: load(userId) });
  },

  toggleBook: (slug) => {
    const { userId, books } = get();
    // No signed-in owner means nothing to write against. Silent: Settings is
    // unreachable without a session, so this is a guard, not a user path.
    if (!userId || !SLUG.test(slug)) return;
    const next = sanitizePersonalBooks(books.includes(slug) ? books.filter((b) => b !== slug) : [...books, slug]);
    set({ books: next });
    persist(userId, next);
  },

  reset: () => {
    const { userId } = get();
    if (!userId) return;
    set({ books: NO_BOOKS });
    persist(userId, NO_BOOKS);
  },
}));

/** The selection to USE: empty unless it belongs to the viewer. */
export function selectOwnedPersonalBooks(
  state: { userId: string | null; books: readonly string[] },
  viewerId: string | null,
): readonly string[] {
  return viewerId != null && state.userId === viewerId ? state.books : NO_BOOKS;
}

/**
 * Clear the in-memory selection; the persisted one is left intact, so signing
 * back in restores it. Called from `authStore.purgeUserCaches`, which runs on
 * every path to unauthenticated, including the cross-tab logout handler.
 */
export function resetPersonalBooks(): void {
  usePersonalBooks.getState().hydrateFor(null);
}

/**
 * Cross-tab sync, scoped to the current owner's key. The `storage` event
 * fires only in OTHER tabs, so re-reading cannot loop; the key check keeps
 * unrelated writes elsewhere in the app from re-rendering the table.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    const { userId } = usePersonalBooks.getState();
    if (!userId || e.key !== storageKeyFor(userId)) return;
    usePersonalBooks.setState({ books: load(userId) });
  });
}
